import React, { useEffect, useState } from 'react';

/**
 * A product thumbnail that opens full-size on tap (Brad, 2026-09-22: "Its hard to see in
 * the little picture if you are trying to see the products"). The thumbnail is a button
 * so it works from the keyboard; the enlarged view is a fixed overlay above the dialogs
 * (they sit at z-index 2000). Clicks stop here so tapping the picture never selects the
 * row behind it.
 *
 * THE BACK BUTTON CLOSES IT (Brad, 2026-09-23: "there was no way to exit the pic, so I
 * clicked my back button, which took me clear back to the bins section"). Opening pushes
 * one history entry; a back press pops it and closes the picture, nothing else. There is
 * also a visible Close, and a tap anywhere still closes.
 */
interface Props {
  src: string;
  alt?: string;
  size?: number;
  fit?: 'contain' | 'cover';
}

const ZoomImage: React.FC<Props> = ({ src, alt = '', size = 44, fit = 'contain' }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    window.history.pushState({ zoomImage: true }, '');
    let closedByBack = false;
    const onPop = () => { closedByBack = true; setOpen(false); };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      // Closed by a tap: take our entry off the history so the next back is a real one.
      if (!closedByBack && window.history.state && window.history.state.zoomImage) window.history.back();
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        aria-label="See the picture larger"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        style={{ width: size, height: size, padding: 0, border: 'none', background: '#fff', borderRadius: 6, cursor: 'zoom-in', flexShrink: 0 }}
      >
        <img src={src} alt={alt} loading="lazy" style={{ width: size, height: size, objectFit: fit, borderRadius: 6, display: 'block' }} />
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Picture"
          onClick={(e) => { e.stopPropagation(); setOpen(false); }}
          style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, cursor: 'zoom-out' }}
        >
          <button
            type="button"
            aria-label="Close the picture"
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            style={{ position: 'absolute', top: 12, right: 12, minHeight: 44, minWidth: 44, padding: '0 14px', borderRadius: 22, border: 'none', background: '#fff', color: '#111', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
          >
            ✕ Close
          </button>
          <img src={src} alt={alt} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', background: '#fff', borderRadius: 8 }} />
          <div style={{ position: 'absolute', bottom: 16, left: 0, right: 0, textAlign: 'center', color: '#fff', fontSize: 13, opacity: 0.8 }}>Tap anywhere to close</div>
        </div>
      )}
    </>
  );
};

export default ZoomImage;
